import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { InputGroupPropsTypes } from "../../inputComp/InputGroupType";
import InputGroup from "../../inputComp/InputGroup";
import {
	AddressIcon,
	AreaSpecificityIcon,
	CloseIcon,
	DepartmentIcon,
	EmailIcon,
	FaxIcon,
	IdIcon,
	MobileIcon,
	NameIcon,
	SearchIcon,
} from "../../iconSet/IconSet";
import LoadingScreen from "../../loadingScreenComp/LoadingScreen";
import { Role, RoleInterface, DepartmentOption } from "./RoleTypes";
import axios from "axios";
import { SelectItemTypes } from "../../../CommonTypes";
import AlertDialog from "../../popupComp/AlertDialog";
import useInputValidate from "../../../hooks/useInputValidate";

import loadLightIcon from '../../../images/icons/load-light.gif'
import loadDarkIcon from '../../../images/icons/load-dark.gif'
import { useTheme } from "../../../context/ThemeContext";

const Roles = () => {
	
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { theme } = useTheme()
	
	const [alertDialogState, setAlertDialogState] = useState(false);
	const [alertTitle, setAlertTitle] = useState('');
	const [alertDescription, setAlertDescription] = useState('');
	const [alertCallback, setAlertCallback] = useState<() => void | undefined>();

	const [areaSpecificity, setAreaSpecificity] = useState<SelectItemTypes[]>([
		{name: "Whole country", value: "whole_country"},
		{name: "Area Specific", value: "area_specific"}
	])

	const showAlertDialog = (title: string, description: string, callback?: () => void) => {
		setAlertTitle(title);
		setAlertDescription(description);
		setAlertCallback(() => callback); // wrap in function to preserve reference
		setAlertDialogState(true);
	};

	const [isDataPickerOpen, setIsDataPickerOpen] = useState<boolean>(false);
	const [isDataPickerLoading, setIsDataPickerLoading] = useState<boolean>(false);

	const allRoles = useRef<Role[]>([]);
	const [filteredRoleData, setFilteredRoleData] = useState<Role[]>([]);

	const roleNameRef = useRef<HTMLInputElement | null>(null);
	const roleIdRef = useRef<HTMLInputElement | null>(null);
	const roleDescriptionRef = useRef<HTMLInputElement | null>(null);

	const roleAreaAuthoritySpecificityRef = useRef<HTMLSelectElement | null>(null);

	const [form, setForm] = useState<Role>({
		roleid: "",
		rolename: "",
		roledescription: "",
		roleAreaSpecificity: ""
	});

	const [isUpdate, setIsUpdate] = useState(false);

	

	const BASE_URL = process.env.REACT_APP_BASE_URL

	const [refreshKey, setRefreshKey] = useState(0);

	const handleChange = async (e: any) => {
		const { name, value } = e.target;
		setForm((prevForm) => ({
			...prevForm,
			[name]: value,
		}));
	};

	const handleChangeSelect = async (e: ChangeEvent<HTMLSelectElement>) => {
		setForm((prevForm) => ({
			...prevForm,
			roleAreaSpecificity: e.target.value
		}));
	};

	// existing data change handle
	const handleChangeExistingDataSearch = (
		event: ChangeEvent<HTMLInputElement>
	): void => {
		if (event.target.value === "") {
			setFilteredRoleData(allRoles.current);
		} else {
			setFilteredRoleData(
				allRoles.current.filter((item) =>
					item.rolename.toLowerCase().includes(event.target.value.toLowerCase()) ||
					item.roleAreaSpecificity.toLowerCase().includes(event.target.value.toLowerCase())
				)
			);
		}
	};

	const handleBlur = async () => {
		const token = localStorage.getItem("access_token");
		if (form.roleid.trim() !== "") {
			try {
				//const res = await axios.get<RoleInterface>(`https://bos.beconx.live/api/v1/getrolebyid/${form.roleid}`,
				const res = await axios.get<RoleInterface>(BASE_URL + `/identity_manager/api/v1/getrolebyid/${form.roleid}`,
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);
				if (res.data) {
					//console.log("RESPONSE -> ", res)
					setForm(res.data);
					setIsUpdate(true);
				} else {
					setForm({
						roleid: form.roleid,
						rolename: "",
						roledescription: "",
						roleAreaSpecificity: ""
					});
					setIsUpdate(false);
				}
			} catch {
				setIsUpdate(false);
			}
		}
	};

	const formItems: InputGroupPropsTypes[] = [
		{
			type: "text",
			icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
			color: "var(--color-accent-bg)",
			placeholder: "Enter role id",
			label: "role id",
			name: "roleid",
			componentInputElement: roleIdRef,
			dataPicker: true,
			isDataPickerOpen: isDataPickerOpen,
			setIsDataPickerOpen: setIsDataPickerOpen,
			onChange: handleChange,
			onBlur: handleBlur,
			value: form.roleid,
			required: true
		},
		{
			type: "text",
			icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
			color: "var(--color-accent-bg)",
			placeholder: "Enter role name",
			label: "role name",
			name: "rolename",
			componentInputElement: roleNameRef,
			onChange: handleChange,
			required: true,
			value: form.rolename,
		},
		{
			type: "text",
			icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
			color: "var(--color-accent-bg)",
			placeholder: "Enter short name",
			label: "Role description",
			name: "roledescription",
			componentInputElement: roleDescriptionRef,
			onChange: handleChange,
			value: form.roledescription,
		},
		{
			type: "select",
			icon: <AreaSpecificityIcon width="1.5rem" color="var(--color-main-white)" />,
			color: "var(--color-accent-bg)",
			placeholder: "Select role area specificity",
			label: "Area authority specificity",
			name: "area_authority_specificity",
			optionList: areaSpecificity,
			componentSelectElement: roleAreaAuthoritySpecificityRef,
			value: form.roleAreaSpecificity,
			onChange: handleChangeSelect,
			required: true
		},
	];

	// Handle clicking filtered item
	const handleClickFilteredOrganization = (role: Role): void => {
		if (
			roleNameRef.current &&
			roleIdRef.current &&
			roleDescriptionRef.current  &&
			roleAreaAuthoritySpecificityRef.current
		) {
			roleIdRef.current.value = role.roleid.toString();
			roleNameRef.current.value = role.rolename.toString();
			roleDescriptionRef.current.value = role.roledescription;
			roleAreaAuthoritySpecificityRef.current.value = role.roleAreaSpecificity
		}
		// Update form state
		setForm({
			roleid: role.roleid.toString(),
			rolename: role.rolename.toString(),
			roledescription: role.roledescription,
			roleAreaSpecificity: role.roleAreaSpecificity
		});
		setIsDataPickerOpen(false);
		setIsUpdate(true);
	};

	//Handle Create and Update Role
	const handleCreateRole = async () => {
		
		const token = localStorage.getItem("access_token");
		if (!form.roleid || !form.rolename) {
			showAlertDialog("Missing Fields", "ID and Name are required.");
			return;
		}

		try {
			
			setIsLoading(true)
			//const response = await axios.post("https://bos.beconx.live/api/v1/createrole", 
			const response = await axios.post(BASE_URL + "/identity_manager/api/v1/createrole",
				form, 
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);
			//console.log("FORM DATA ->", form);

			showAlertDialog(isUpdate ? "Success" : "Created", isUpdate ? "✅ Role updated." : "✅ Role created.");

			setForm({
				roleid: "",
				rolename: "",
				roledescription: "",
				roleAreaSpecificity: ""
			});
			setIsUpdate(false);
			setRefreshKey((prev) => prev + 1);
		} catch (error) {
			console.error("Error creating/updating role:", error);
			showAlertDialog("Error", "❌ Error creating/updating role");
		}
		finally{
			setIsLoading(false)
		}
	};

	// Handle Delete Role
	const handleDeleteRole = async () => {
		const token = localStorage.getItem("access_token");
		if (!token) {
			showAlertDialog("Unauthorized", "Missing access token.");
			return;
		}

		try {
			setIsLoading(true)
			//const response = await fetch(`https://bos.beconx.live/api/v1/roledelete/${form.roleid}`,
			const response = await fetch(BASE_URL + `/identity_manager/api/v1/roledelete/${form.roleid}`,
				{
					method: "DELETE",
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.ok) {
				const resultText = await response.text();
				showAlertDialog("Deleted", resultText);
				setForm({
					roleid: "",
					rolename: "",
					roledescription: "",
					roleAreaSpecificity: ""
				});
				setIsUpdate(false);
			} else {
				const contentType = response.headers.get("content-type");
				if (contentType && contentType.includes("application/json")) {
					const error = await response.json();
					console.log(error.message || "Error deleting user");
				} else {
					const errorText = await response.text();
					console.log(errorText);
				}
			}
		} catch (err) {
			console.error("Delete failed", err);
			console.log("An error occurred during deletion");
		}
		finally{
			setIsLoading(false)
		}
	};

	// Get all available organization
	const getAllRoles = () => {
		setIsDataPickerLoading(true)
		const token = localStorage.getItem("access_token");
		//fetch("https://bos.beconx.live/api/v1/getallroles", {
		fetch(BASE_URL + "/identity_manager/api/v1/getallroles", {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		})
			.then((res) => res.json())
			.then((data) => {
				allRoles.current = data;
				setFilteredRoleData(data);
			})
			.catch((err) => console.error("Error fetching roles:", err))
			.finally(() => {
				setIsDataPickerLoading(false)
			})
	};

	useEffect(() => {
		getAllRoles();
	}, [isUpdate, refreshKey]);

	return (
		<div className="container-wrapper">
			<span className="title">Setup roles</span>
			<div className="create-department">
				<div className="grid-layout-create-department">
					<div className="left-side">
						{formItems.slice(0, 2).map((field, index) => (
							<InputGroup key={index} {...field} />
						))}
					</div>

					<div className="right-side">
						{formItems.slice(2).map((field, index) => (
							<InputGroup key={index} {...field} />
						))}
					</div>

					<div className="button-wrapper" style={{ gridArea: "button" }}>
						<div className="left-side"></div>
						<div className="right-side">
							<button
								className="button-normal"
								onClick={(e) => {
									showAlertDialog(
										"Confirm submit",
										"Are you sure you want to submit this role?",
										handleCreateRole
									)
								}}
							>
								{isUpdate ? "Update" : "Create"}
							</button>
							{isUpdate && (
								<button
									className="button-normal"
									style={{ backgroundColor: "var(--color-main-red)" }}
									onClick={(e) => 
										showAlertDialog(
											"Confirm Delete",
											"Are you sure you want to delete this role?",
											handleDeleteRole
										)
									}
									//handleDeleteRole()
								>
									Delete
								</button>
							)}
						</div>
					</div>

					{isDataPickerOpen && (
						<div className="data-picker-table-wrapper">
							<div className="data-picker-table-container">
								<div className="search-wrapper">
									<input
										type="text"
										placeholder="Search Role"
										onChange={(e) => handleChangeExistingDataSearch(e)}
									/>
									<div className="search-icon">
										<SearchIcon width="1rem" color="var(--color-main-white)" />
									</div>
									<div
										className="close-icon"
										onClick={(e) => setIsDataPickerOpen(false)}
									>
										<CloseIcon width="1.5rem" color="var(--color-text)" />
									</div>
								</div>
								<div className="row header">
									<span style={{ width: '50%' }}>Role</span>
									<span style={{ width: '50%' }}>Area Specificity</span>
								</div>

								{isDataPickerLoading ? (
									<div className="searching-data">
										<img src={theme === 'dark' ? loadDarkIcon : theme === 'light' ? loadLightIcon : theme === 'colorful' ? loadLightIcon : ''} alt="" />
										<span>Searching role data...</span>
									</div>
									) : (
										<div className="data-wrapper">
										{filteredRoleData.map((role, index) => (
											<div
												key={index}
												className="row"
												onClick={(e) => handleClickFilteredOrganization(role)}
											>
												<span>{role.rolename ? role.rolename : 'N/A'}</span>
												<span>{role.roleAreaSpecificity === "whole_country" ? "Whole country" : role.roleAreaSpecificity === "area_specific" ? "Area specific" : "N/A"}</span>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}
					{alertDialogState && (
						<AlertDialog
							title={alertTitle}
							description={alertDescription}
							windowState={alertDialogState}
							setWindowState={setAlertDialogState}
							function={alertCallback}
						/>
					)}
				</div>
			</div>
			{isLoading ? <LoadingScreen /> : null}
		</div>
	);
};

export default Roles;
